import { camelCase } from '@/lib/change-case'

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/

const isRecursable = (value: unknown): value is Record<string, unknown> | unknown[] =>
  value !== null
  && typeof value === 'object'
  && !(value instanceof Date)
  && !(value instanceof RegExp)
  && !(value instanceof Error)

function keyExcluded (key: string, exclude: Array<string | RegExp> | undefined): boolean {
  if (!exclude?.length) return false
  for (const pattern of exclude) {
    if (typeof pattern === 'string') {
      if (pattern === key) return true
    } else {
      pattern.lastIndex = 0
      if (pattern.test(key)) return true
    }
  }
  return false
}

function camelizeInner (input: unknown, options: { deep: boolean; exclude?: Array<string | RegExp> }): unknown {
  if (Array.isArray(input)) {
    return input.map((item) => camelizeInner(item, options))
  }
  if (!isRecursable(input)) {
    return input
  }
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    let v: unknown = value
    if (options.deep && isRecursable(value)) {
      v = camelizeInner(value, options)
    }
    const newKey = keyExcluded(key, options.exclude) ? key : camelCase(key)
    out[newKey] = v
  }
  return out
}

type CamelCaseStr<S extends string> = S extends `${infer P}_${infer C}${infer R}`
  ? `${P}${Uppercase<C>}${CamelCaseStr<R>}`
  : S

export type CamelizeKeys<T> = T extends readonly (infer U)[]
  ? CamelizeKeys<U>[]
  : T extends Record<string, unknown>
    ? { [K in keyof T as K extends string ? CamelCaseStr<K> : K]: CamelizeKeys<T[K]> }
    : T

export const camelizeKeys = <T extends Record<string, unknown> | ReadonlyArray<Record<string, unknown>>>(val: T) =>
  camelizeInner(val, {
    deep: true,
    exclude: [DATE_KEY],
  }) as CamelizeKeys<T>
